import React, { useState, useRef, useEffect } from 'react';
import {
	editLogoImage,
	fetchMyBusiness,
} from '../../../store/actions/business';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import headerImage from 'assets/img/image_placeholder.jpg';
import {
	Container,
	Row,
	Col,
	Card,
	CardBody,
	CardTitle,
	Button,
	UncontrolledTooltip,
} from 'reactstrap';

import Header from './SectionHeader';
import ProductForms from '../../Modals/ProductForms';
import AboutEditModal from '../../Modals/AboutEdit';
import ContactEditModal from '../../Modals/ContactEdit';
import SocialMediaEditModal from '../../Modals/SocialMediaEdit';
import FooterAuth from 'views/Components/Footer';

import * as yup from 'yup';

const FILE_SIZE = 1e6;
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'];

const schema = yup.object({
	files: yup
		.mixed()
		.nullable()
		.required('Image is required')
		.test(
			'fileSize',
			'File too large, select Images smaller than 1MB',
			(value) => value && value.size <= FILE_SIZE,
		)
		.test(
			'fileFormat',
			'Unsupported Format',
			(value) => value && SUPPORTED_FORMATS.includes(value.type),
		),
});

function SectionProducts(rest) {
	const dispatch = useDispatch();
	const business = useSelector((state) => state.myBusiness);
	const { id } = useSelector((state) => state.currentUser.user);

	const [image, setImage] = useState(undefined);
	const [validIt, setValidIt] = useState(undefined);

	useEffect(() => {
		if (id) {
			dispatch(fetchMyBusiness(id)).catch((err) => {
				console.error('Failed to fetch business data:', err);
			});
		}
	}, [id, dispatch]);

	useEffect(() => {
		try {
			dispatch(rest.removeError());
			dispatch(rest.removeSuccess());
		} catch (err) {
			console.log(err);
		}
	}, []);

	const inputFile = useRef(null);

	const handleClickImage = () => {
		dispatch(editLogoImage(id, image));
		setImage(undefined);
	};

	const handleClearInputFile = () => {
		inputFile.current.value = '';
		setImage('');
	};

	document.documentElement.classList.remove('nav-open');

	useEffect(() => {
		document.body.classList.add('profile-page');
		return function cleanup() {
			document.body.classList.remove('profile-page');
		};
	});

	return (
		<>
			<Header business={business} />

			<div className='public-editor-page'>
				<Container fluid='lg'>
					<div className='public-editor-hero-card'>
						<div className='public-editor-brand'>
							<div className='public-editor-logo-wrap'>
								{!image && (
									<>
										<img
											className='public-editor-logo'
											alt='Business logo'
											src={business.logo || headerImage}
										/>

										<Button
											className='public-editor-logo-edit'
											color='success'
											id='tooltip437036653'
											size='sm'
											onClick={() => inputFile.current.click()}
											type='button'>
											<i className='nc-icon nc-settings-gear-65' />
										</Button>

										<UncontrolledTooltip
											delay={0}
											placement='right'
											target='tooltip437036653'>
											Edit logo
										</UncontrolledTooltip>
									</>
								)}

								<input
									ref={inputFile}
									type='file'
									style={{ display: 'none' }}
									onChange={(e) => {
										setImage(e.target.files[0]);
										setValidIt(undefined);
										schema
											.validate({
												files: e.target.files[0],
											})
											.catch((err) => {
												setImage(undefined);
												setValidIt(err.errors);
											});
									}}
								/>

								{image && (
									<div className='post-preview'>
										<img
											className='post-preview-img'
											src={URL.createObjectURL(image)}
											alt='preview'
										/>
										<i
											onClick={handleClearInputFile}
											className='bi bi-x-circle'
										/>
										<i
											onClick={handleClickImage}
											className='bi bi-check-circle'
										/>
									</div>
								)}
							</div>

							<div className='public-editor-brand-text'>
								<h1>{business.businessName}</h1>
								<p>
									{business.subTitle ||
										business.businessSector ||
										'Your storefront'}
								</p>
								{validIt && (
									<h6 className='errorMessageHeaderImage'>{validIt}</h6>
								)}
							</div>
						</div>
					</div>

					{rest.errors?.message && (
						<div className='alert alert-danger mt-3'>{rest.errors.message}</div>
					)}

					<section className='public-editor-section'>
						<div className='public-editor-section__header'>
							<div>
								<h3 className='public-editor-section__title'>Latest Offers</h3>
								<p className='public-editor-section__subtitle'>
									Products displayed on your public website
								</p>
							</div>
							<ProductForms />
						</div>

						<Row>
							{business.products?.map((product) => (
								<Col xl='4' lg='6' md='6' sm='12' key={product._id}>
									<Card className='public-product-card'>
										<div className='public-product-card__image-wrap'>
											<Link
												to={{ pathname: `/my-product-page/${product._id}` }}>
												<img
													alt={product.title}
													className='public-product-card__image'
													src={product.image?.[0]}
												/>
											</Link>
										</div>

										<CardBody>
											<div className='public-product-card__body'>
												<CardTitle tag='h5'>{product.title}</CardTitle>
												<p className='public-product-card__description'>
													{product.description}
												</p>
												<div className='public-product-card__price'>
													CD$ {product.price}
												</div>
											</div>
										</CardBody>
									</Card>
								</Col>
							))}
						</Row>
					</section>

					<section className='public-editor-section'>
						<div className='public-info-card'>
							<div className='public-info-card__header'>
								<div>
									<h3>About Your Business</h3>
									<p>
										Tell visitors who you are and what makes your business
										special.
									</p>
								</div>
								<AboutEditModal businessAbout={business.about} />
							</div>

							<p className='public-info-card__content'>
								{business.about ||
									'Tell your local customers a brief about yourself.'}
							</p>
						</div>
					</section>

					<section className='public-editor-section public-editor-section--bottom'>
						<Row>
							<Col lg='6' md='12'>
								<div className='public-info-card public-info-card--contact'>
									<div className='public-info-card__header'>
										<div>
											<h3>Contact Information</h3>
											<p>
												These details appear toward the end of your public page.
											</p>
										</div>
										<ContactEditModal
											valuePhone={business.phone}
											valueWebsite={business.website}
										/>
									</div>

									<div className='public-contact-list'>
										<p>
											<i className='bi bi-geo-alt-fill' />{' '}
											<span>{business.address}</span>
										</p>
										<p>
											<i className='bi bi-phone-fill' />{' '}
											<span>{business.phone}</span>
										</p>
										<p>
											<i className='bi bi-globe-americas' />{' '}
											<a
												href={business.website}
												target='_blank'
												rel='noreferrer'>
												{business.website || 'Add your website'}
											</a>
										</p>
									</div>
								</div>
							</Col>

							<Col lg='6' md='12'>
								<div className='public-info-card public-info-card--social'>
									<div className='public-info-card__header'>
										<div>
											<h3>Social Media</h3>
											<p>Help visitors discover and follow your business.</p>
										</div>
										<SocialMediaEditModal
											valueTwitter={business.twitter}
											valueInstagram={business.instagram}
											valueFacebook={business.facebook}
										/>
									</div>

									<div className='public-social-links'>
										<Button
											className='btn-just-icon'
											color='primary'
											size='sm'
											target='_blank'
											href={business.facebook}
											type='button'>
											<i className='bi bi-facebook' />
										</Button>

										<Button
											className='btn-just-icon'
											color='warning'
											size='sm'
											target='_blank'
											href={business.instagram}
											type='button'>
											<i className='bi bi-instagram' />
										</Button>

										<Button
											className='btn-just-icon'
											target='_blank'
											color='secondary'
											href={business.twitter}
											size='sm'
											type='button'>
											<i className='bi bi-twitter-x' />
										</Button>
									</div>
								</div>
							</Col>
						</Row>
					</section>
				</Container>
			</div>

			<FooterAuth />
		</>
	);
}

export default SectionProducts;
